import { useState, useLayoutEffect } from "react"
import { useLoaderData, useLocation, useNavigate, useOutletContext } from "react-router-dom"
import { useAuth } from "contexts/auth-context"
import Form from "./form"
import toast from "react-hot-toast"
import { useSWRConfig } from "swr"
import { usePage } from "contexts/page-context"
import { Transition } from "components"
import { executeAPI } from "repositories/db-repository"
import { lang } from "config"
import { getIsAdmin, getIsSupervisor, getIsOwner } from "helpers/general-helper"

/**
 * EDIT RETURN
 * - Loader data diasumsikan mengembalikan struktur:
 *   {
 *     return_header: [ { ... } ],
 *     return_item: [...],
 *     return_item_side_by_side: [...],
 *     purchase_header: [...],               // opsional (untuk BY_PO)
 *     purchase_item_side_by_side: [...]     // opsional (untuk BY_PO)
 *   }
 */
export default function Edit() {
  const { user } = useAuth()
  const { cache } = useSWRConfig()
  const { setTransition } = usePage()
  const { setTitle } = useOutletContext()

  const dataFromLoader = useLoaderData()
  const location = useLocation()
  const navigate = useNavigate()

  const [errors, setErrors] = useState({})

  const is_admin = getIsAdmin()
  const is_supervisor = getIsSupervisor()
  const is_owner = getIsOwner()

  // -------- Helpers: ambil header aktif --------
  const header = Array.isArray(dataFromLoader?.data) ? dataFromLoader.data[0] : dataFromLoader?.data
  const currentStatus = header?.latest_flow_detail_id || header?.flow_detail_id || "RETURN_DRAFT"
  const returnId = header?.id

  // -------- Set judul + auto-set IN_REVIEW bila supervisor/owner membuka dokumen yang perlu direview --------
  useLayoutEffect(() => {
    setTransition(location.state?.transition)

    const goReviewBySupervisor =
      (currentStatus === "RETURN_NEED_REVIEW" || currentStatus === "RETURN_IN_REVIEW") && is_supervisor == 1
    const goReviewByOwner =
      currentStatus === "RETURN_APPROVED" && (is_owner == 1 || is_supervisor == 1)

    if (returnId && (goReviewBySupervisor || goReviewByOwner)) {
      // upgrade status tampilan ke IN_REVIEW agar konsisten
      executeAPI(
        `${process.env.REACT_APP_REMAX_PS_API_URL}`,
        "update_return_header",
        {
          id: returnId,
          latest_flow_detail_id: "RETURN_IN_REVIEW",
          flow_detail_id: "RETURN_IN_REVIEW",
          record_status: "U",
          updated_by: user?.name
        },
        () => {},
        () => {}
      )
      setTitle(`Review ${returnId}`)
    } else if (currentStatus === "RETURN_DRAFT" && is_admin == 1) {
      setTitle(`Edit ${returnId}`)
    } else {
      setTitle(`View ${returnId}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnId, currentStatus])

  // -------- Abstraksi updater utama (header only) --------
  const updateReturnHeader = (payload, onSuccess, onError) => {
    executeAPI(
      `${process.env.REACT_APP_REMAX_PS_API_URL}`,
      "update_return_header",
      {
        id: payload?.id,
        origin: payload?.origin ?? "RT",
        outlet_group_id: payload?.outlet_group_id,
        outlet_group_name: payload?.outlet_group_name,
        outlet_group_phone_number: payload?.outlet_group_phone_number,
        user_id: payload?.user_id,
        user_name: payload?.user_name,
        outlet_id: payload?.outlet_id,
        outlet_name: payload?.outlet_name,
        outlet_phone_number: payload?.outlet_phone_number,
        outlet_email_address: payload?.outlet_email_address,
        entry_date: payload?.entry_date,

        purchase_header_id: payload?.purchase_header_id ?? null,
        purchase_transaction_id: payload?.purchase_transaction_id ?? null,

        warehouse_id: payload?.warehouse_id,
        warehouse_name: payload?.warehouse_name,
        warehouse_address: payload?.warehouse_address,

        supplier_id: payload?.supplier_id,
        supplier_name: payload?.supplier_name,
        supplier_phone_number: payload?.supplier_phone_number,
        supplier_email_address: payload?.supplier_email_address,
        supplier_address: payload?.supplier_address,

        notes: payload?.notes,
        ppn_percentage: payload?.ppn_percentage,

        return_type: payload?.return_type,

        latest_flow_detail_id: payload?.latest_flow_detail_id,
        flow_detail_id: payload?.flow_detail_id,

        record_status: "U",
        updated_by: user?.name
      },
      onSuccess,
      onError
    )
  }

  // -------- Tambah/Hapus item (dipanggil dari Form via action) --------
  const addReturnItems = (payload, onSuccess, onError) => {
    executeAPI(
      `${process.env.REACT_APP_REMAX_PS_API_URL}`,
      "insert_return_item",
      {
        id: payload?.id,
        return_header_id: payload?.id,
        arr_return_item: payload?.arr_return_item, // JSON.stringify([{ product_unit_id, product_unit, quantity, price, notes }])
        created_by: user?.name
      },
      onSuccess,
      onError
    )
  }

  const deleteReturnItems = (payload, onSuccess, onError) => {
    executeAPI(
      `${process.env.REACT_APP_REMAX_PS_API_URL}`,
      "delete_return_item",
      {
        id: payload?.id,
        return_header_id: payload?.id,
        arr_return_item: payload?.arr_return_item, // JSON.stringify([{ product_unit_id }])
        updated_by: user?.name
      },
      onSuccess,
      onError
    )
  }

  // -------- Finalizer setelah sukses --------
  const finishAndBack = (message, isError = false) => {
    cache.clear()
    navigate({ pathname: '/return' })
    const fn = isError ? toast.error : toast.success
    fn(message, { id: "submit" })
  }

  // -------- Validasi ringan --------
  const validateHeader = (d) => {
    const e = {
      warehouse_name: d.warehouse_name ? undefined : lang.deliver_to + " " + lang.field_required,
      supplier_name: d.supplier_name ? undefined : lang.supplier_name + " " + lang.field_required,
      warehouse_address: d.warehouse_address ? undefined : lang.delivery_address + " " + lang.field_required,
    }
    setErrors(e)
    // true kalau semua undefined
    return Object.values(e).every((v) => v === undefined)
  }

  const buildReturnToInventoryPayload = (payload) => {
        // Ambil semua item return yang sudah ada di halaman (side_by_side > raw)
        const rows = (dataFromLoader?.return_item_side_by_side ?? dataFromLoader?.return_item ?? payload.rowsForTable ??[]);
        const items = rows.map(r => ({
            product_unit_id: r.product_unit_id,
            product_unit: r.product_unit,
            quantity: Number(r.rtn_quantity ?? r.quantity ?? 0),
            price: Number(r.rtn_price ?? r.price ?? 0),
            notes: r.notes ?? null,
        })).filter(i => i.product_unit_id && i.quantity > 0);
        console.log(payload?.purchase_transaction_id,"payload?.purchase_transaction_id")
        return {
            return_header_id: payload?.id,
            purchase_order_id: payload?.purchase_transaction_id,
            outlet_id: payload?.outlet_id,
            arr_return_item: JSON.stringify(items),
            created_by: user?.name
        };
    };

  // -------- Handler utama dipass ke <Form onSubmit={handleSubmit} /> --------
  const handleSubmit = (payload) => {
    // routing ACTION dari Form (ADD_RETURN_ITEM / DELETE_RETURN_ITEM) atau update header/workflow
    const action = payload?.action

    const onSuccessHeader = () => {
      finishAndBack(payload?.message || "Return berhasil disimpan!")
    }
    const onErrorHeader = (msg) => {
      finishAndBack("onError: " + (msg || "Gagal menyimpan return"), true)
    }

    const onSuccessItem = () => {
    //   finishAndBack(payload?.message || "Item return berhasil diproses!")
        toast.success(payload?.message || "Item return berhasil diproses!", { id: "submit" });
        navigate(0); // refresh current page
    }
    const onErrorItem = (msg) => {
        const fn = toast.error
        fn(msg, { id: "submit" })
    }

    // Aksi Item (langsung tembak endpoint khusus item)
    if (action === "ADD_RETURN_ITEM") {
      toast.loading("Menambah item...", { id: "submit" })
      return addReturnItems(payload, onSuccessItem, onErrorItem)
    }
    if (action === "DELETE_RETURN_ITEM") {
      toast.loading("Menghapus item...", { id: "submit" })
      return deleteReturnItems(payload, onSuccessItem, onErrorItem)
    }

    // Aksi Header / Workflow
    if (!validateHeader(payload)) {
      return
    }
    
    if (payload?.flow_detail_id === "RETURN_CLOSED") {
        toast.loading("Memproses pengembalian stok ke inventory...", { id: "submit" });

        const invPayload = buildReturnToInventoryPayload(payload);
        // Safety: jika tidak ada item yang valid, blok agar tidak close kosong
        const parsed = JSON.parse(invPayload.arr_return_item || "[]");
        
        if (parsed.length === 0) {
          toast.error("Tidak ada item return yang dapat diproses.", { id: "submit" });
          return;
        }

        const onInvSuccess = () => {
        // lanjut update header -> CLOSED
        updateReturnHeader(payload, onSuccessHeader, onErrorHeader);
        };
        const onInvError = (msg) => {
        // Gagal proses inventory -> jangan close
        toast.error(`Gagal proses inventory: ${msg || "Unknown error"}`, { id: "submit" });
        };

        return executeAPI(
        `${process.env.REACT_APP_INVENTORY_API_URL}`,
        "purchase-return/process_data_to_inventory",
        invPayload,
        onInvSuccess,
        onInvError
        );
    }

    toast.loading("Processing Return...", { id: "submit" })

    // Contoh: kalau kamu butuh branch khusus (mis. CLOSE melakukan sesuatu ke Inventory), bisa taruh disini:
    // if (payload.latest_flow_detail_id === "RETURN_COMPLETE") { ... }

    updateReturnHeader(payload, onSuccessHeader, onErrorHeader)
  }

  return (
    <Transition type="fade">
      <Form data={dataFromLoader} errors={errors} onSubmit={handleSubmit} />
    </Transition>
  )
}
